/**
 * All AlertTypes that a shipment can have.
 *
 * This should match the list defined in ShipmentAlert.ALERT_TYPE_CHOICES,
 * in backend/shipment/models.py - except for the 'flight_delayed'
 * constant (see comment below).
 */
export type AlertType =
  | 'low_battery_at_origin'
  | 'driver_late_to_pickup'
  | 'flight_delayed'
  // This is called 'flight_delayed' in ALERT_TYPE_CHOICES, but the
  // backend handles automatically mapping 'flight_delayed_beyond_threshold'
  // to 'flight_delayed'.
  | 'flight_delayed_beyond_threshold'
  | 'shipment_not_loaded_onto_flight'
  | 'sensor_location_still_at_pickup'
  | 'sensor_location_not_at_drop'
  | 'offload_delay_after_flight_arrival'
  | 'driver_late_to_recover_shipment'
  | 'driver_cant_find_location'
  | 'delivered_to_wrong_address'
  | 'delivered_to_wrong_recipient'
  | 'low_battery'
  | 'flight_landing_close_to_cargo_closing_time'
  | 'weather_event_impact_expected'
  | 'geopolitical_event_impact_expected'
  | 'status_update_overdue'
  | 'vehicle_idle_in_transit'
  | 'temperature_nearing_limit'
  | 'temperature_excursion_detected'
  | 'temperature_excursion_pre_alarm'
  | 'viability_window_risk_expiring'
  | 'viability_at_risk_before_arrival'
  | 'viability_window_nearly_exhausted'
  | 'viability_window_exhausted'
  | 'excessive_light_exposure_detected'
  | 'excessive_tilt_detected'
  | 'excessive_pressure_detected'
  | 'excessive_humidity_detected'
  | 'excessive_shock_detected'
  | 'proof_of_delivery_missing'
  | 'delivery_overdue'
  | 'vessel_delayed_or_cancelled'
  | 'route_deviation_detected'
  | 'customer_not_available_or_business_closed'
  | 'incorrect_address'
  | 'rescheduled_by_recipient'
  | 'rescheduled_by_transport_provider'
  | 'delivery_restrictions'
  | 'refused_by_recipient'
  | 'weather_impact'
  | 'customs_issue'
  | 'cancelled_by_sender'
  | 'operational_delay'
  | 'other'
  | 'transport_provider_exception';

/**
 * Human-readable labels for each AlertType.
 */
export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  low_battery_at_origin: 'Low battery at origin',
  driver_late_to_pickup: 'Pickup overdue',
  flight_delayed: 'Flight delayed or cancelled',
  flight_delayed_beyond_threshold: 'Flight delayed',
  shipment_not_loaded_onto_flight: 'Shipment not loaded onto flight',
  sensor_location_still_at_pickup: 'Sensor location still at pickup',
  sensor_location_not_at_drop: 'Sensor location not at drop',
  offload_delay_after_flight_arrival: 'Offload delay after flight arrival',
  driver_late_to_recover_shipment: 'Driver late to recover shipment',
  driver_cant_find_location: "Driver can't find location",
  delivered_to_wrong_address: 'Incorrect delivery location',
  delivered_to_wrong_recipient: 'Delivered to wrong recipient',
  low_battery: 'Low battery',
  flight_landing_close_to_cargo_closing_time: 'Cargo cut-off warning',
  weather_event_impact_expected: 'Weather event impact expected',
  geopolitical_event_impact_expected: 'Geopolitical risk',
  status_update_overdue: 'Status update overdue',
  vehicle_idle_in_transit: 'Shipment not moving',
  temperature_nearing_limit: 'Temperature nearing limit',
  temperature_excursion_detected: 'Temperature excursion',
  temperature_excursion_pre_alarm: 'Temperature excursion (pre-alarm)',
  viability_window_risk_expiring: 'Viability window warning',
  viability_at_risk_before_arrival: 'Viability at risk before arrival',
  viability_window_nearly_exhausted: 'Viability window nearly exhausted',
  viability_window_exhausted: 'Viability window expired',
  excessive_light_exposure_detected: 'Light exposure',
  excessive_tilt_detected: 'Tilt threshold exceeded',
  excessive_pressure_detected: 'Pressure range exceeded',
  excessive_humidity_detected: 'Humidity range exceeded',
  excessive_shock_detected: 'Shock threshold exceeded',
  proof_of_delivery_missing: 'Proof of delivery (POD) missing',
  delivery_overdue: 'Delivery overdue',
  transport_provider_exception: 'Transport provider exception',
  route_deviation_detected: 'Route deviation detected',
  customer_not_available_or_business_closed: 'Recipient unavailable',
  incorrect_address: 'Incorrect address',
  rescheduled_by_recipient: 'Rescheduled by recipient',
  rescheduled_by_transport_provider: 'Rescheduled by transport provider',
  delivery_restrictions: 'Delivery restrictions',
  refused_by_recipient: 'Refused by recipient',
  weather_impact: 'Weather impact',
  customs_issue: 'Customs issue',
  cancelled_by_sender: 'Cancelled or returned to sender',
  operational_delay: 'Operational delay',
  other: 'Other',
  vessel_delayed_or_cancelled: 'Vessel delayed or cancelled',
};

/**
 * Get the human-readable label for an AlertType.
 */
export function getAlertTypeLabel(alertType: AlertType): string {
  return ALERT_TYPE_LABELS[alertType];
}

/**
 * Prefer the canonical label for a known alert_type; fall back to stored alert_name.
 */
export function getAlertDisplayName(
  alertType: AlertType | string | undefined,
  fallback?: string
): string {
  if (
    alertType &&
    Object.prototype.hasOwnProperty.call(ALERT_TYPE_LABELS, alertType)
  ) {
    return ALERT_TYPE_LABELS[alertType as AlertType];
  }
  return fallback ?? '';
}
